/* eslint-disable react/display-name */
import { Button, Form, Image, Input, Select, Space, Tag, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import styles from 'src/styles/layout.module.css';
import { UploadFile } from 'antd/es/upload/interface';
import { useForm } from 'antd/es/form/Form';
import { CompanyBannerType } from 'src/features/companyBanners/companyBanners.types';
import { CloseOutlined, PictureOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'src/index';
import { createCompanyBanner, fetchCompanyBanners } from 'src/features/companyBanners/companyBanners.state';
import { useCompanyBanners, useCompanyBannersState } from 'src/features/companyBanners/companyBanners.hooks';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';
import tileStyles from 'src/styles/tile.module.css';
import { formatBannerType } from 'src/utils/utils';
import { RoleEnum } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';

type NewBanner = { description: string; fileList: UploadFile[] | undefined; bannerType: CompanyBannerType };

interface CreateBannerProps {
  onCreate: (req: NewBanner) => void;
}

const CreateBannerForm: React.FC<CreateBannerProps> = props => {
  const { onCreate } = props;
  const [form] = useForm();

  const onFinish = (values: NewBanner) => onCreate(values);

  return (
    <Form
      form={form}
      layout={'vertical'}
      onFinish={onFinish}
      initialValues={{ bannerType: CompanyBannerType.AdBanner }}
    >
      <Form.Item name='description' label={'Описание'} required>
        <Input type='text' name='description' placeholder='Введите описание' autoFocus />
      </Form.Item>
      <Form.Item name='bannerType' label={'Тип'} required>
        <Select defaultActiveFirstOption={true} placeholder='Выбирете тип'>
          <Select.Option value={CompanyBannerType.AdBanner} key={CompanyBannerType.AdBanner}>
            {formatBannerType(CompanyBannerType.AdBanner)}
          </Select.Option>
          <Select.Option value={CompanyBannerType.SystemBanner} key={CompanyBannerType.SystemBanner}>
            {formatBannerType(CompanyBannerType.SystemBanner)}
          </Select.Option>
          <Select.Option value={CompanyBannerType.CompanyBanner} key={CompanyBannerType.CompanyBanner}>
            {formatBannerType(CompanyBannerType.CompanyBanner)}
          </Select.Option>
        </Select>
      </Form.Item>
      <Form.Item shouldUpdate>
        {() => (
          <Button
            type='primary'
            htmlType='submit'
            disabled={
              !form.isFieldsTouched(['description', 'type']) ||
              !!form.getFieldsError().filter(({ errors }) => errors.length).length
            }
          >
            Submit
          </Button>
        )}
      </Form.Item>
    </Form>
  );
};

const CompanyBannersTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const [createMode, setCreateMode] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();
  const banners = useCompanyBanners();
  const state = useCompanyBannersState();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const history = useHistory();

  useEffect(() => {
    dispatch(fetchCompanyBanners({ companyId }));
  }, [dispatch, companyId]);

  const createHandler = useCallback(
    async (data: NewBanner) => {
      const response = await dispatch(
        createCompanyBanner({
          companyId,
          data: {
            description: data.description,
            bannerType: data.bannerType,
          },
        })
      );
      if (createCompanyBanner.fulfilled.match(response)) {
        setCreateMode(false);
        history.push(`/commercial/companies/${companyId}/banners/${response.payload.id}`);
      }
    },
    [dispatch, companyId, history]
  );
  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography.Title level={4}>Баннеры</Typography.Title>
        <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}>
          {createMode ? (
            <Button onClick={() => setCreateMode(false)} icon={<CloseOutlined />} />
          ) : (
            <Button onClick={() => setCreateMode(true)} icon={<PlusOutlined />} />
          )}
        </ComponentRoleProtected>
      </div>
      <div className={styles.searchWrap}>
        <Input
          type='text'
          value={search}
          placeholder='Введите название'
          style={{ marginRight: 8 }}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <div className={styles.content}>
        {createMode && <CreateBannerForm onCreate={createHandler} />}
        <LoaderWrapper loading={state.isPending}>
          {banners.map(b => (
            <Link
              key={b.id}
              className={tileStyles.listTile}
              style={{ height: '85px' }}
              to={`/commercial/companies/${companyId}/banners/${b.id}`}
            >
              <Space direction={'vertical'} size={1}>
                <Space>
                  <PictureOutlined />
                  <span>{b.description}</span>
                </Space>
                <Typography.Text type={'secondary'} style={{ fontSize: 12, margin: 0 }}>
                  {formatBannerType(b.bannerType)}
                </Typography.Text>
              </Space>
              <Space align='center'>
                {b.images.length === 0 ? (
                  <Tag color={'red'}>Пустой</Tag>
                ) : (
                  <Space>
                    {b.images.slice(0, 5).map(url => (
                      <div
                        key={url}
                        style={{
                          width: 60,
                          height: 60,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Image src={url} width={'100%'} preview={false} />
                      </div>
                    ))}
                  </Space>
                )}
              </Space>
            </Link>
          ))}
        </LoaderWrapper>
      </div>
    </div>
  );
};

export default CompanyBannersTab;
